import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

interface CTASectionProps {
  headline: string;
  subheadline?: string;
  ctaText: string;
  ctaHref: string;
  urgencyText?: string;
  secondaryText?: string;
}

// Icon components
const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
  </svg>
);

const RocketIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
  </svg>
);

const ShieldIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
  </svg>
);

// What you get items
const whatYouGet = [
  {
    icon: CalendarIcon,
    title: '45-Minute Strategy Session',
    description: 'Deep-dive into your specific AI opportunities',
  },
  {
    icon: RocketIcon,
    title: 'Custom Implementation Roadmap',
    description: 'Prioritized action plan with timelines',
  },
  {
    icon: ShieldIcon,
    title: 'ROI Projections',
    description: 'Specific dollar amounts for each opportunity',
  },
];

// Stats for social proof
const stats = [
  { value: '127+', label: 'Companies Helped' },
  { value: '$12M+', label: 'Savings Delivered' },
  { value: '23', label: 'Days Avg Deploy' },
];

export const CTASection: React.FC<CTASectionProps> = ({
  headline,
  subheadline,
  ctaText,
  ctaHref,
  urgencyText,
  secondaryText,
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  return (
    <section ref={sectionRef} className="relative py-16 sm:py-24 md:py-32 bg-[#0a0a0a] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

        {/* Gradient glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-gradient-to-r from-emerald-500/10 via-blue-500/10 to-violet-500/10 rounded-full blur-[120px] opacity-50" />

        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
              backgroundSize: '48px 48px',
            }}
          />
        </div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main content */}
        <div className="text-center mb-16">
          {/* Section badge */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            <span className="text-emerald-400 text-sm font-medium">Start Your AI Journey</span>
          </motion.div>

          {/* Headline */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6"
          >
            {headline}
          </motion.h2>

          {subheadline && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base sm:text-lg md:text-xl text-zinc-400 mb-6 sm:mb-8 max-w-2xl mx-auto px-4 sm:px-0"
            >
              {subheadline}
            </motion.p>
          )}
        </div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-start">
          {/* Left: What you get */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-zinc-800/50 p-5 sm:p-6 md:p-8">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <CheckIcon className="w-4 h-4 text-emerald-400" />
                </span>
                What You'll Get
              </h3>

              <div className="space-y-5">
                {whatYouGet.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                    className="flex gap-4"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-zinc-800/50 border border-zinc-700/30 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-zinc-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">{item.title}</h4>
                      <p className="text-zinc-500 text-sm">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Risk-free guarantee */}
              <div className="mt-8 pt-6 border-t border-zinc-800/50">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center mt-0.5">
                    <ShieldIcon className="w-3.5 h-3.5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-emerald-400 font-medium text-sm">100% Risk-Free</p>
                    <p className="text-zinc-500 text-sm">No obligation. No pressure. Just a straightforward conversation about your AI opportunities.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: CTA card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/20 via-blue-500/20 to-violet-500/20 rounded-2xl blur-xl opacity-50" />

              <div className="relative bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-emerald-500/20 p-5 sm:p-6 md:p-8">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-8 pb-8 border-b border-zinc-800/50">
                  {stats.map((stat, index) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 10 }}
                      animate={isInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                      className="text-center"
                    >
                      <p className="text-lg sm:text-xl md:text-2xl font-bold text-white">{stat.value}</p>
                      <p className="text-zinc-500 text-xs">{stat.label}</p>
                    </motion.div>
                  ))}
                </div>

                {/* CTA content */}
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-white mb-2">Ready to Deploy AI?</h3>
                  <p className="text-zinc-400 text-sm mb-6">Book your free strategy session today</p>

                  {/* Main CTA button */}
                  <motion.a
                    href={ctaHref}
                    className="group w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-zinc-900 font-semibold rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-white/10 mb-4"
                    whileHover={{ y: -2, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {ctaText}
                    <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </motion.a>

                  {/* Secondary text */}
                  {secondaryText && (
                    <p className="text-zinc-500 text-xs">{secondaryText}</p>
                  )}
                </div>

                {/* Urgency indicator */}
                {urgencyText && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : {}}
                    transition={{ duration: 0.5, delay: 0.7 }}
                    className="mt-6 pt-6 border-t border-zinc-800/50"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                      </span>
                      <p className="text-amber-400 text-sm font-medium">{urgencyText}</p>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom trust indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-12 flex flex-wrap items-center justify-center gap-8"
        >
          {[
            { icon: '30min', text: 'Free 30-min call' },
            { icon: 'nda', text: 'NDA protected' },
            { icon: 'calendar', text: 'Flexible scheduling' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-2 text-zinc-500 text-sm">
              <div className="w-5 h-5 rounded-full bg-zinc-800 flex items-center justify-center">
                <CheckIcon className="w-3 h-3 text-zinc-500" />
              </div>
              <span>{item.text}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
